import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';
const APP_VERSION = import.meta.env.VITE_APP_VERSION || '1.8.0';

/**
 * Get or create a persistent anonymous client ID for quota tracking.
 */
function getClientId(): string {
  const CLIENT_ID_KEY = 'ats_anon_id';
  try {
    const existing = localStorage.getItem(CLIENT_ID_KEY);
    if (existing) return existing;
    const id = crypto.randomUUID();
    localStorage.setItem(CLIENT_ID_KEY, id);
    return id;
  } catch {
    return 'unknown';
  }
}

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'X-Client-Type': 'web-app',
    'X-Client-Version': APP_VERSION,
    'X-Client-Id': getClientId(),
  },
  timeout: 30000, // 30 seconds
});

let getMode: () => 'local' | 'cloud' = () => 'cloud';
export const setApiModeResolver = (resolver: () => 'local' | 'cloud') => {
  getMode = resolver;
};

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('accessToken');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// Quota exceeded event — UI listens for this to show registration/upgrade wall
export type QuotaExceededReason = 'registration_required' | 'limit_reached' | 'upgrade_required';

export interface QuotaExceededEvent {
  reason: QuotaExceededReason;
  tier: string;
  operation: string;
  used: number;
  limit: number;
  resetsAt: string;
}

type QuotaExceededListener = (event: QuotaExceededEvent) => void;
const quotaListeners: QuotaExceededListener[] = [];

/** Subscribe to quota_exceeded events (for QuotaWall component). */
export function onQuotaExceeded(listener: QuotaExceededListener): () => void {
  quotaListeners.push(listener);
  return () => {
    const idx = quotaListeners.indexOf(listener);
    if (idx >= 0) quotaListeners.splice(idx, 1);
  };
}

function emitQuotaExceeded(data: QuotaExceededEvent): void {
  quotaListeners.forEach((fn) => fn(data));
}

// Global refresh lock to prevent concurrent refresh attempts
let isRefreshing = false;
let refreshSubscribers: Array<(token: string) => void> = [];

function onTokenRefreshed(token: string) {
  refreshSubscribers.forEach((cb) => cb(token));
  refreshSubscribers = [];
}

function addRefreshSubscriber(cb: (token: string) => void) {
  refreshSubscribers.push(cb);
}

// Response interceptor to handle token refresh and quota exceeded
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    // Handle 429 quota_exceeded responses
    if (error.response?.status === 429) {
      const data = error.response.data as Record<string, unknown> | undefined;
      if (data && data.error === 'quota_exceeded') {
        emitQuotaExceeded({
          reason: (data.reason as QuotaExceededReason) || 'limit_reached',
          tier: (data.tier as string) || 'anonymous',
          operation: (data.operation as string) || 'unknown',
          used: (data.used as number) || 0,
          limit: (data.limit as number) || 0,
          resetsAt: (data.resetsAt as string) || '',
        });
        return Promise.reject(error);
      }
    }

    // In local mode we don't redirect on 401; just propagate the error
    if (getMode() === 'local') {
      return Promise.reject(error);
    }

    // If error is 401 and we haven't retried yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      // If already refreshing, queue this request to retry after refresh completes
      if (isRefreshing) {
        return new Promise((resolve) => {
          addRefreshSubscriber((newToken: string) => {
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
            }
            resolve(apiClient(originalRequest));
          });
        });
      }

      isRefreshing = true;

      try {
        const refreshToken = localStorage.getItem('refreshToken');
        if (!refreshToken) {
          throw new Error('No refresh token available');
        }

        // Try to refresh the token
        const response = await axios.post(`${API_BASE_URL}/api/v1/auth/refresh`, {
          refreshToken,
        });

        const { accessToken, refreshToken: newRefreshToken } = response.data.data;

        // Store new tokens
        localStorage.setItem('accessToken', accessToken);
        if (newRefreshToken) {
          localStorage.setItem('refreshToken', newRefreshToken);
        }

        isRefreshing = false;
        onTokenRefreshed(accessToken);

        // Retry the original request with new token
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        }
        return apiClient(originalRequest);
      } catch (refreshError) {
        isRefreshing = false;
        refreshSubscribers = [];
        // Refresh failed, redirect to login
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// Helper function to handle API errors
export function handleApiError(error: unknown): string {
  if (axios.isAxiosError(error)) {
    if (error.response?.data?.message) {
      return error.response.data.message;
    }
    if (error.message) {
      return error.message;
    }
  }
  return 'An unexpected error occurred';
}

// Generic API response type
// Canonical shared type: @shared/types/common.ts (data?: T optional)
// Local version keeps data: T required for simpler consumer code.
export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

export interface ApiError {
  success: false;
  message: string;
  errors?: Record<string, string[]>;
}
